// TutorialOverlay.js - Step-by-step interactive tutorial (27 steps)

import React, { useMemo, useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import THEME from '../../styles/theme';

// Format description text: **text** becomes bold, {{PN:text}} becomes red bold part number
const formatDescription = (text) => {
  if (!text) return '';
  return text
    .replace(/\{\{PN:([^}]+)\}\}/g, '<span style="color: #ff6b6b; font-weight: 700;">$1</span>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
};

const tutorialSteps = [
  // Module 1: Getting Started (Steps 1-6)
  {
    id: 'welcome',
    module: 'Getting Started',
    title: 'Welcome to GChain!',
    description: 'GChain helps you design and analyze RF signal chains. In this tutorial, you\'ll learn to build circuits, analyze performance across frequency, and export professional reports. Let\'s get started!',
    target: 'center',
    position: 'center',
    highlight: null
  },
  {
    id: 'pause-intro',
    module: 'Getting Started',
    title: 'Pause Anytime',
    description: 'This tutorial has 27 steps. If you need to step away, click the "Pause" button to save your progress. You can resume later from the Help menu. Now let\'s explore the interface!',
    target: 'center',
    position: 'center',
    highlight: null
  },
  {
    id: 'palette',
    module: 'Getting Started',
    title: 'Component Selector',
    description: 'This is your component library. Components are organized by category: Sources, Amplifiers, Filters, Mixers, and more. You will be able to use the search bar at the top to quickly find specific components.',
    target: 'center',
    position: 'center',
    highlight: { left: 0, top: 60, width: 250, height: 'calc(100vh - 60px)' },
    arrow: { target: 'left-panel-top', direction: 'down' }
  },
  {
    id: 'add-source',
    module: 'Getting Started',
    title: 'Add a Signal Generator',
    description: 'Let\'s add your first component! Click on "Signal Generator" under the Sources category to add it to the canvas.',
    target: 'center',
    position: 'center',
    highlight: { left: 0, top: 60, width: 250, height: 'calc(100vh - 60px)' },
    waitForAction: 'add-node',
    waitForType: 'inputSource',
    arrow: { target: 'signal-generator', direction: 'left' }
  },
  {
    id: 'canvas-nav',
    module: 'Getting Started',
    title: 'Navigating the Canvas',
    description: 'Use your mouse wheel or click the + and - buttons in the lower left to zoom in and out. Click and drag on empty space to pan around. Try it now!',
    target: 'center',
    position: 'center',
    highlight: { left: 250, top: 60, width: 'calc(100vw - 250px)', height: 'calc(100vh - 60px)' },
    waitForAction: 'viewport-change'
  },
  {
    id: 'add-measurement',
    module: 'Getting Started',
    title: 'Add a Measurement Component',
    description: 'Measurement nodes show calculated results at specific points in your chain. Click on "Measurement" in the Measurement category to add one.',
    target: 'center',
    position: 'center',
    highlight: { left: 0, top: 60, width: 250, height: 'calc(100vh - 60px)' },
    waitForAction: 'add-node',
    waitForType: 'measurement',
    arrow: { target: 'measurement', direction: 'left' }
  },

  // Module 2: Building Signal Chains (Steps 7-14)
  {
    id: 'move-component',
    module: 'Building Signal Chains',
    title: 'Moving Components',
    description: 'Components can be repositioned by clicking and dragging them. Move the Measurement node to the right of the Signal Generator to prepare for connecting them.',
    target: 'center',
    position: 'center',
    highlight: { left: 250, top: 60, width: 'calc(100vw - 250px)', height: 'calc(100vh - 60px)' },
    waitForAction: 'position-correct'
  },
  {
    id: 'connect',
    module: 'Building Signal Chains',
    title: 'Connecting Components',
    description: 'To connect components, drag from the output port (right side, blue dot) of one component to the input port (left side) of another. Connect your Signal Generator to the Measurement node now.',
    target: 'center',
    position: 'center',
    // Allow canvas interaction for connecting
    highlight: { left: 250, top: 60, width: 'calc(100vw - 250px)', height: 'calc(100vh - 60px)' },
    waitForAction: 'add-edge'
  },
  {
    id: 'chain-mode',
    module: 'Building Signal Chains',
    title: 'Enable Chain Mode',
    description: 'Click the chain link icon in the left panel header to enable Chain Mode. When ON, new components automatically connect to your existing chain.',
    target: 'center',
    position: 'center',
    highlight: { left: 0, top: 60, width: 250, height: 120 },
    waitForAction: 'enable-chain-mode',
    arrow: { target: 'chain-mode-toggle', direction: 'left' }
  },
  {
    id: 'add-lna',
    module: 'Building Signal Chains',
    title: 'Add a Generic LNA',
    description: 'Now add a generic Low Noise Amplifier (LNA) from the Amplifiers category. With Chain Mode ON, it will automatically insert between your source and measurement.',
    target: 'center',
    position: 'center',
    highlight: { left: 0, top: 60, width: 250, height: 'calc(100vh - 60px)' },
    waitForAction: 'add-node',
    waitForType: 'lna',
    arrow: { target: 'lna', direction: 'left' }
  },
  {
    id: 'copy-paste',
    module: 'Building Signal Chains',
    title: 'Copy & Paste',
    description: 'Click on a component to select it, then press Ctrl+C to copy and Ctrl+V to paste. Try copying and pasting the LNA now.',
    target: 'center',
    position: 'center',
    highlight: { left: 250, top: 60, width: 'calc(100vw - 250px)', height: 'calc(100vh - 60px)' },
    waitForAction: 'paste-node'
  },
  {
    id: 'undo-redo',
    module: 'Building Signal Chains',
    title: 'Undo & Redo',
    description: 'Made a mistake? Press Ctrl+Z to undo. Press it now to undo your last action.',
    target: 'center',
    position: 'center',
    highlight: { left: 250, top: 60, width: 'calc(100vw - 250px)', height: 'calc(100vh - 60px)' },
    waitForAction: 'undo'
  },
  {
    id: 'compression',
    module: 'Building Signal Chains',
    title: 'Compression Indicators',
    description: 'Component OP1dB values change color based on compression: White = normal, Yellow = warning, Orange = compressed. Try it! Increase the Signal Generator\'s Power value until the LNA\'s OP1dB turns yellow or orange.',
    target: 'center',
    position: 'center',
    highlight: { left: 250, top: 60, width: 'calc(100vw - 250px)', height: 'calc(100vh - 60px)' },
    waitForAction: 'compression-triggered'
  },

  // Module 3: Component Parameters (Steps 13-15)
  {
    id: 'edit-params',
    module: 'Component Parameters',
    title: 'Manually Editing Parameter Values',
    description: 'Single-click on any underlined parameter value to edit it. Try changing the LNA\'s Gain value now.',
    target: 'center',
    position: 'center',
    highlight: { left: 250, top: 60, width: 'calc(100vw - 250px)', height: 'calc(100vh - 60px)' },
    waitForAction: 'edit-param'
  },
  {
    id: 'palette-tooltips',
    module: 'Component Parameters',
    title: 'Component Selector Tooltips',
    description: 'Notice we\'ve switched to "All Components" view. Hover over any Catalog component (one with a Part Number) in the left panel to see a quick summary of its specifications.',
    target: 'center',
    position: 'center',
    highlight: { left: 0, top: 60, width: 250, height: 'calc(100vh - 60px)' },
    waitForAction: 'catalog-hover'
  },
  {
    id: 'add-catalog-part',
    module: 'Component Parameters',
    title: 'Add a Catalog Part',
    description: 'Let\'s add a real catalog part. Find and click on the {{PN:GRF2070}} LNA in the Amplifiers category to add it to the canvas.',
    target: 'center',
    position: 'center',
    highlight: { left: 0, top: 60, width: 250, height: 'calc(100vh - 60px)' },
    waitForAction: 'add-catalog-part'
  },
  {
    id: 'context-menus',
    module: 'Component Parameters',
    title: 'View Datasheet',
    description: 'Right-click on the GRF2070 you just added and select "View Datasheet" to open it in a new tab. Close the new tab when done to return to GChain. This works for any Guerrilla RF catalog component.',
    target: 'center',
    position: 'center',
    highlight: { left: 250, top: 60, width: 'calc(100vw - 250px)', height: 'calc(100vh - 60px)' },
    waitForAction: 'view-datasheet'
  },

  // Module 4: Frequency Analysis (Steps 17-21)
  {
    id: 'freq-modes',
    module: 'Frequency Analysis',
    title: 'Single vs Sweep Mode',
    description: 'GChain supports two frequency analysis modes: Single Frequency for quick calculations at one point, and Frequency Sweep for analyzing performance across a range. Click the broom icon (to the left of the frequency value) on the Signal Generator to enable Sweep mode.',
    target: 'center',
    position: 'center',
    highlight: { left: 250, top: 60, width: 'calc(100vw - 250px)', height: 'calc(100vh - 60px)' },
    waitForAction: 'enable-sweep-mode',
    arrow: { target: 'sweep-mode-toggle', direction: 'up' }
  },
  {
    id: 'sweep-values',
    module: 'Frequency Analysis',
    title: 'Values in Sweep Mode',
    description: 'When in Frequency Sweep mode, component values will show "(sweep)" rather than a single value because no single value now satisfies the input condition. However, the over-frequency info is now available as plots in the Measurement component.',
    target: 'center',
    position: 'center',
    highlight: { left: 250, top: 60, width: 'calc(100vw - 250px)', height: 'calc(100vh - 60px)' },
    arrow: { target: 'measurement', direction: 'down' }
  },
  {
    id: 'sweep-setup',
    module: 'Frequency Analysis',
    title: 'Setting Up a Sweep',
    description: 'In Sweep mode, you\'ll be able to set your Start and Stop frequencies plus the step size. The system calculates performance at each frequency point, automatically interpolating component data as needed.',
    target: 'center',
    position: 'center',
    highlight: { left: 0, top: 0, width: '100%', height: 60 }
  },
  {
    id: 'mini-plots',
    module: 'Frequency Analysis',
    title: 'Mini-Plots on Measurement Nodes',
    description: 'In Sweep mode, Measurement nodes display compact frequency response graphs. Click on "View Details" in the Measurement node to open the full Plot Panel.',
    target: 'center',
    position: 'center',
    highlight: { left: 250, top: 60, width: 'calc(100vw - 250px)', height: 'calc(100vh - 60px)' },
    waitForAction: 'mini-plot-click'
  },
  {
    id: 'plot-panel',
    module: 'Frequency Analysis',
    title: 'Frequency Response Plots',
    description: 'The Plot Panel shows full-size graphs of gain, noise figure, P1dB, and OIP3 versus frequency. Note: only frequencies applicable to ALL components in your signal chain are displayed to conserve x-axis space.',
    target: 'center',
    position: 'center',
    highlight: null,
    onComplete: 'close-plot-panel'
  },

  // Module 5: Catalog & Data Sources (Steps 22-25)
  {
    id: 'find-similar',
    module: 'Catalog & Data Sources',
    title: 'Find Similar Catalog Parts',
    description: 'See the small book icon in the upper right of the Generic LNA component? Click it to find similar catalog parts that match your specs.',
    target: 'center',
    position: 'center',
    highlight: { left: 250, top: 60, width: 'calc(100vw - 250px)', height: 'calc(100vh - 60px)' },
    waitForAction: 'find-similar-click',
    arrow: { target: 'recommendation-badge', direction: 'down' }
  },
  {
    id: 'replace-part',
    module: 'Catalog & Data Sources',
    title: 'Replace with Catalog Part',
    description: 'The dropdown shows catalog parts with similar specs to your generic component. Select one to see a comparison table, then click "Replace" to swap it in with one click!',
    target: 'center',
    position: 'center',
    highlight: { left: 250, top: 60, width: 'calc(100vw - 250px)', height: 'calc(100vh - 60px)' },
    waitForAction: 'part-replaced'
  },
  {
    id: 'data-sources',
    module: 'Catalog & Data Sources',
    title: 'Data Sources & Voltage',
    description: 'Catalog components use up to three data sources: Manual (you enter values), S2P (Touchstone files), or Database (pre-loaded tables). Try it now: on the {{PN:GRF2070}}, change the Voltage dropdown to 3.3V and watch the Measurement values update.',
    target: 'center',
    position: 'center',
    highlight: { left: 250, top: 60, width: 'calc(100vw - 250px)', height: 'calc(100vh - 60px)' },
    waitForAction: 'voltage-changed'
  },
  {
    id: 'smith-chart',
    module: 'Catalog & Data Sources',
    title: 'Smith Chart Visualization',
    description: 'For components with S2P data, you can view S-parameters on a Smith Chart, as Log Magnitude plots, or both. Look for the Smith Chart button on catalog components and click it.',
    target: 'center',
    position: 'center',
    highlight: { left: 250, top: 60, width: 'calc(100vw - 250px)', height: 'calc(100vh - 60px)' },
    waitForAction: 'smith-chart-click',
    arrow: { target: 'smith-chart-badge', direction: 'down' }
  },

  // Module 6: Projects, Export & Feedback (Steps 26-27)
  {
    id: 'save-export',
    module: 'Projects, Export & Feedback',
    title: 'Saving & Exporting',
    description: 'You can click "Save" at any time to save your project. You can use the project dropdown to switch between projects. Now try exporting to PDF - click the File menu, then "Create PDF" to generate a professional report.',
    target: 'center',
    position: 'center',
    highlight: { left: 0, top: 0, width: '100%', height: 60 },
    waitForAction: 'pdf-exported',
    noBlockers: true  // Disable blockers entirely for this step to allow dropdown access
  },
  {
    id: 'feedback',
    module: 'Projects, Export & Feedback',
    title: 'Updates & Feedback',
    description: 'Click the Help menu, then click "Submit Feedback" to submit bug reports or feature requests - you can even attach a screenshot. Check "Recent Updates" to see what\'s new. Open the Feedback modal now to complete the tutorial!',
    target: 'center',
    position: 'center',
    highlight: { left: 0, top: 0, width: '100%', height: 60 },
    waitForAction: 'feedback-opened',
    arrow: { target: 'help-menu-button', direction: 'right' },
    noBlockers: true  // Disable blockers entirely for this step to allow dropdown access
  }
];

// Track what the user has done to validate waitForAction requirements
const useActionTracker = (nodes, edges, currentStep, stepConfig, tutorialActions) => {
  return useMemo(() => {
    if (!stepConfig?.waitForAction) {
      return { actionRequired: false, actionCompleted: true };
    }

    const action = stepConfig.waitForAction;
    const waitForType = stepConfig.waitForType;

    if (action === 'add-node') {
      // Check if a node of the required type exists
      if (waitForType) {
        const hasNodeOfType = nodes.some(n => n.type === waitForType);
        return { actionRequired: true, actionCompleted: hasNodeOfType };
      }
      // Just check if any node was added (compare to expected count based on step)
      return { actionRequired: true, actionCompleted: nodes.length > 0 };
    }

    if (action === 'add-edge') {
      return { actionRequired: true, actionCompleted: edges.length > 0 };
    }

    // Check if Measurement node is positioned to the right of Signal Generator
    if (action === 'position-correct') {
      const sigGen = nodes.find(n => n.type === 'inputSource');
      const measurement = nodes.find(n => n.type === 'measurement');
      if (sigGen && measurement) {
        // Measurement's x position should be greater than SigGen's x position
        const isCorrectlyPositioned = measurement.position.x > sigGen.position.x;
        return { actionRequired: true, actionCompleted: isCorrectlyPositioned };
      }
      return { actionRequired: true, actionCompleted: false };
    }

    // Store-tracked actions
    if (action === 'move-node') {
      return { actionRequired: true, actionCompleted: tutorialActions?.nodeMoved || false };
    }

    if (action === 'viewport-change') {
      return { actionRequired: true, actionCompleted: tutorialActions?.viewportChanged || false };
    }

    if (action === 'copy-node') {
      return { actionRequired: true, actionCompleted: tutorialActions?.nodeCopied || false };
    }

    if (action === 'paste-node') {
      return { actionRequired: true, actionCompleted: tutorialActions?.nodePasted || false };
    }

    if (action === 'undo') {
      return { actionRequired: true, actionCompleted: tutorialActions?.undoPerformed || false };
    }

    if (action === 'edit-param') {
      return { actionRequired: true, actionCompleted: tutorialActions?.paramEdited || false };
    }

    if (action === 'catalog-hover') {
      return { actionRequired: true, actionCompleted: tutorialActions?.catalogHovered || false };
    }

    if (action === 'add-catalog-part') {
      // Check if a GRF2070 node exists
      const hasGRF2070 = nodes.some(n => n.type === 'GRF2070' || n.data?.partNumber === 'GRF2070');
      return { actionRequired: true, actionCompleted: hasGRF2070 };
    }

    if (action === 'view-datasheet') {
      return { actionRequired: true, actionCompleted: tutorialActions?.datasheetViewed || false };
    }

    if (action === 'context-menu') {
      return { actionRequired: true, actionCompleted: tutorialActions?.contextMenuOpened || false };
    }

    if (action === 'mini-plot-click') {
      return { actionRequired: true, actionCompleted: tutorialActions?.miniPlotClicked || false };
    }

    if (action === 'find-similar-click') {
      return { actionRequired: true, actionCompleted: tutorialActions?.findSimilarClicked || false };
    }

    if (action === 'part-replaced') {
      return { actionRequired: true, actionCompleted: tutorialActions?.partReplaced || false };
    }

    if (action === 'smith-chart-click') {
      return { actionRequired: true, actionCompleted: tutorialActions?.smithChartClicked || false };
    }

    if (action === 'enable-chain-mode') {
      return { actionRequired: true, actionCompleted: tutorialActions?.chainModeEnabled || false };
    }

    if (action === 'compression-triggered') {
      return { actionRequired: true, actionCompleted: tutorialActions?.compressionTriggered || false };
    }

    if (action === 'enable-sweep-mode') {
      return { actionRequired: true, actionCompleted: tutorialActions?.sweepModeEnabled || false };
    }

    if (action === 'voltage-changed') {
      return { actionRequired: true, actionCompleted: tutorialActions?.voltageChanged || false };
    }

    if (action === 'feedback-opened') {
      return { actionRequired: true, actionCompleted: tutorialActions?.feedbackOpened || false };
    }

    if (action === 'pdf-exported') {
      return { actionRequired: true, actionCompleted: tutorialActions?.pdfExported || false };
    }

    return { actionRequired: false, actionCompleted: true };
  }, [nodes, edges, currentStep, stepConfig, tutorialActions]);
};

// Map arrow targets to component type data attributes or element IDs
const ARROW_TARGET_MAP = {
  'signal-generator': 'inputSource',
  'measurement': 'measurement',
  'lna': 'lna',
  'chain-mode-toggle': 'chain-mode-toggle',
  'help-menu-button': 'help-menu-button',
  'left-panel-top': 'left-panel-container',
  'sweep-mode-toggle': 'sweep-mode-toggle',
  'recommendation-badge': 'recommendation-badge',
  'smith-chart-badge': 'smith-chart-badge'
};

const TutorialOverlay = ({ currentStep, onNext, onPrevious, onSkip, onComplete, onPause, nodes = [], edges = [], tutorialActions = {} }) => {
  const step = tutorialSteps[currentStep];
  const [arrowPosition, setArrowPosition] = useState(null);
  const [tooltipOffset, setTooltipOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

  const { actionRequired, actionCompleted } = useActionTracker(nodes, edges, currentStep, step, tutorialActions);

  // NOTE: Do NOT reset tooltip position when step changes - user's drag position should persist

  // Handle tooltip drag
  const handleDragStart = (e) => {
    if (e.target.closest('button') || e.target.closest('input')) return; // Don't drag from buttons
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      offsetX: tooltipOffset.x,
      offsetY: tooltipOffset.y
    };
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      setTooltipOffset({
        x: dragStartRef.current.offsetX + dx,
        y: dragStartRef.current.offsetY + dy
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Dynamically find arrow target element position
  // Show arrow for: steps with required actions that aren't completed, OR informational steps (no waitForAction)
  useEffect(() => {
    const isInformationalStep = !step?.waitForAction;
    const shouldShowArrow = step?.arrow?.target && (isInformationalStep || !actionCompleted);

    if (!shouldShowArrow) {
      setArrowPosition(null);
      return;
    }

    const findTargetPosition = () => {
      const targetKey = ARROW_TARGET_MAP[step.arrow.target];
      if (!targetKey) return;

      // Try to find element by data-component-type first, then by ID
      let targetElement = document.querySelector(`[data-component-type="${targetKey}"]`);
      if (!targetElement) {
        targetElement = document.getElementById(targetKey);
      }

      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        const direction = step.arrow.direction || 'left';
        if (direction === 'right') {
          // Arrow comes from left, pointing right at the target
          setArrowPosition({
            top: rect.top + rect.height / 2 - 14,
            left: rect.left - 120,
            direction: 'right'
          });
        } else if (direction === 'down') {
          // Arrow comes from above, pointing down at the target
          setArrowPosition({
            top: rect.top - 60,
            left: rect.left + rect.width / 2 - 50,
            direction: 'down'
          });
        } else if (direction === 'up') {
          // Arrow comes from below, pointing up at the target
          setArrowPosition({
            top: rect.bottom + 8,
            left: rect.left + rect.width / 2 - 50,
            direction: 'up'
          });
        } else {
          // Default: Arrow comes from right, pointing left at the target
          setArrowPosition({
            top: rect.top + rect.height / 2 - 14,
            left: rect.right + 8,
            direction: 'left'
          });
        }
      }
    };

    // Initial find
    findTargetPosition();

    // Re-find on window resize
    window.addEventListener('resize', findTargetPosition);

    // Also re-check periodically in case the element appears after a short delay
    const interval = setInterval(findTargetPosition, 200);
    const timeout = setTimeout(() => clearInterval(interval), 2000);

    return () => {
      window.removeEventListener('resize', findTargetPosition);
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [step?.arrow?.target, actionCompleted]);

  // Note: Auto-advance disabled - user must click Next to proceed
  // This gives users time to see the success state and control their own pace

  if (!step) return null;

  const isLastStep = currentStep === tutorialSteps.length - 1;
  const isFirstStep = currentStep === 0;

  // Get current module for display
  const currentModule = step.module;

  // Determine if Next button should be disabled
  const isNextDisabled = actionRequired && !actionCompleted;

  // Determine if this step has an interactive area
  const hasInteractiveArea = !!step.highlight;

  // Parse highlight dimensions for blocker positioning
  // Default highlight area for left panel: { left: 0, top: 60, width: 250, height: 'calc(100vh - 60px)' }
  const getBlockerProps = () => {
    if (!step.highlight) return null;

    const h = step.highlight;
    // Convert values - handle both numbers and strings like 'calc(100vh - 60px)'
    const top = typeof h.top === 'number' ? `${h.top}px` : h.top || '0px';
    const left = typeof h.left === 'number' ? `${h.left}px` : h.left || '0px';
    const width = typeof h.width === 'number' ? `${h.width}px` : h.width || '0px';
    const height = typeof h.height === 'number' ? `${h.height}px` : h.height || '0px';

    // For percentage widths like '100%', convert to viewport calculation
    const rightEdge = h.width === '100%' ? '100%' : `calc(${left} + ${width})`;

    return { top, left, width, height, rightEdge };
  };

  const blockerProps = getBlockerProps();

  return (
    <>
      {/* Overlay system - either full blocker or blocker with hole (skip if noBlockers flag set) */}
      {!step.noBlockers && (
        hasInteractiveArea && blockerProps ? (
          <>
            {/* Top blocker - above the interactive area */}
            <BlockerTop $top={blockerProps.top} />
            {/* Bottom blocker - below the interactive area */}
            <BlockerBottom $top={`calc(${blockerProps.top} + ${blockerProps.height})`} />
            {/* Left blocker - to the left of interactive area */}
            <BlockerLeft
              $top={blockerProps.top}
              $left={blockerProps.left}
              $height={blockerProps.height}
            />
            {/* Right blocker - to the right of interactive area */}
            <BlockerRight
              $top={blockerProps.top}
              $right={blockerProps.rightEdge}
              $height={blockerProps.height}
            />
          </>
        ) : (
          /* Full screen blocker when no interactive area */
          <FullBlocker />
        )
      )}

      {/* Visual highlight effect (non-blocking, just visual) */}
      {step.highlight && (
        <HighlightBox style={step.highlight} />
      )}

      {/* Arrow indicator pointing to target element */}
      {step.arrow && arrowPosition && (
        <ArrowIndicator style={{ top: arrowPosition.top, left: arrowPosition.left }} $direction={arrowPosition.direction}>
          {arrowPosition.direction === 'right' ? (
            <>
              <ArrowLabel>{step.waitForAction ? 'Click here!' : 'Look here'}</ArrowLabel>
              <ArrowSvg viewBox="0 0 24 24" $direction="right">
                <path d="M12 4l-8 8h6v8h4v-8h6z" fill={THEME.accentPrimary} />
              </ArrowSvg>
            </>
          ) : arrowPosition.direction === 'down' ? (
            <ArrowIndicatorVertical>
              <ArrowLabel>{step.waitForAction ? 'Click here!' : 'Look here'}</ArrowLabel>
              <ArrowSvg viewBox="0 0 24 24" $direction="down">
                <path d="M12 4l-8 8h6v8h4v-8h6z" fill={THEME.accentPrimary} />
              </ArrowSvg>
            </ArrowIndicatorVertical>
          ) : arrowPosition.direction === 'up' ? (
            <ArrowIndicatorVertical>
              <ArrowSvg viewBox="0 0 24 24" $direction="up">
                <path d="M12 4l-8 8h6v8h4v-8h6z" fill={THEME.accentPrimary} />
              </ArrowSvg>
              <ArrowLabel>{step.waitForAction ? 'Click here!' : 'Look here'}</ArrowLabel>
            </ArrowIndicatorVertical>
          ) : (
            <>
              <ArrowSvg viewBox="0 0 24 24" $direction="left">
                <path d="M12 4l-8 8h6v8h4v-8h6z" fill={THEME.accentPrimary} />
              </ArrowSvg>
              <ArrowLabel>{step.waitForAction ? 'Click here!' : 'Look here'}</ArrowLabel>
            </>
          )}
        </ArrowIndicator>
      )}

      {/* Tutorial tooltip */}
      <TooltipContainer
        position={step.position}
        style={{ transform: `translate(${tooltipOffset.x}px, ${tooltipOffset.y}px)` }}
        onMouseDown={handleDragStart}
        $isDragging={isDragging}
      >
        <DragHint>Drag to move</DragHint>
        <Tooltip>
          <TooltipHeader>
            <HeaderLeft>
              <ModuleLabel>{currentModule}</ModuleLabel>
              <StepIndicator>
                Step {currentStep + 1} of {tutorialSteps.length}
              </StepIndicator>
            </HeaderLeft>
            <HeaderRight>
              <PauseButton onClick={onPause}>Pause</PauseButton>
              <SkipButton onClick={onSkip}>Skip All</SkipButton>
            </HeaderRight>
          </TooltipHeader>

          <TooltipContent>
            <TooltipTitle>{step.title}</TooltipTitle>
            <TooltipDescription dangerouslySetInnerHTML={{ __html: formatDescription(step.description) }} />
            {actionRequired && !actionCompleted && (
              <ActionHint>
                Complete the action above to continue
              </ActionHint>
            )}
            {actionRequired && actionCompleted && (
              <ActionComplete>
                Action completed! Click {isLastStep ? 'Finish Tutorial' : 'Next'} to continue.
              </ActionComplete>
            )}
          </TooltipContent>

          <TooltipFooter>
            <ProgressDots>
              {tutorialSteps.map((_, index) => (
                <Dot key={index} $active={index === currentStep} $completed={index < currentStep} />
              ))}
            </ProgressDots>

            <ButtonGroup>
              {!isFirstStep && (
                <SecondaryButton onClick={onPrevious}>
                  Back
                </SecondaryButton>
              )}

              {!isLastStep ? (
                <PrimaryButton onClick={onNext} disabled={isNextDisabled}>
                  {actionRequired ? (actionCompleted ? 'Next' : 'Waiting...') : 'Next'}
                </PrimaryButton>
              ) : (
                <PrimaryButton onClick={onComplete}>
                  Finish Tutorial
                </PrimaryButton>
              )}
            </ButtonGroup>
          </TooltipFooter>
        </Tooltip>
      </TooltipContainer>
    </>
  );
};

// Full-screen interaction blocker - blocks clicks everywhere EXCEPT the allowed area
// We use 4 divs to create a frame around the interactive hole
const BlockerTop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: ${props => props.$top || '0px'};
  z-index: 9997;
  background: rgba(0, 0, 0, 0.5);
`;

const BlockerBottom = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  top: ${props => props.$top || '100%'};
  z-index: 9997;
  background: rgba(0, 0, 0, 0.5);
`;

const BlockerLeft = styled.div`
  position: fixed;
  top: ${props => props.$top || '0px'};
  left: 0;
  width: ${props => props.$left || '0px'};
  height: ${props => props.$height || '0px'};
  z-index: 9997;
  background: rgba(0, 0, 0, 0.5);
`;

const BlockerRight = styled.div`
  position: fixed;
  top: ${props => props.$top || '0px'};
  right: 0;
  left: ${props => props.$right || '100%'};
  height: ${props => props.$height || '0px'};
  z-index: 9997;
  background: rgba(0, 0, 0, 0.5);
`;

// Simple full-screen blocker for steps with no interactive area
const FullBlocker = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9997;
  background: rgba(0, 0, 0, 0.5);
`;

const pulseAnimation = keyframes`
  0%, 100% {
    border-color: ${THEME.accentPrimary};
    box-shadow: 0 0 20px ${THEME.accentPrimary};
  }
  50% {
    border-color: #FF1744;
    box-shadow: 0 0 30px #FF1744;
  }
`;

const HighlightBox = styled.div`
  position: fixed;
  background: transparent;
  border: 3px solid ${THEME.accentPrimary};
  border-radius: 8px;
  z-index: 9998;
  pointer-events: none;
  animation: ${pulseAnimation} 2s ease-in-out infinite;
`;

const bounceLeftAnimation = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(-8px);
  }
`;

const bounceRightAnimation = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  50% {
    transform: translateX(8px);
  }
`;

const bounceDownAnimation = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(8px);
  }
`;

const bounceUpAnimation = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
`;

const getArrowAnimation = (direction) => {
  if (direction === 'right') return bounceRightAnimation;
  if (direction === 'down') return bounceDownAnimation;
  if (direction === 'up') return bounceUpAnimation;
  return bounceLeftAnimation;
};

const ArrowIndicator = styled.div`
  position: fixed;
  z-index: 10001;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  pointer-events: none;
  animation: ${props => getArrowAnimation(props.$direction)} 1s ease-in-out infinite;
`;

const ArrowIndicatorVertical = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const getArrowRotation = (direction) => {
  if (direction === 'right') return 'rotate(90deg)';
  if (direction === 'down') return 'rotate(180deg)';
  if (direction === 'up') return 'rotate(0deg)';
  return 'rotate(-90deg)';
};

const ArrowSvg = styled.svg`
  width: 28px;
  height: 28px;
  /* Arrow points based on direction */
  transform: ${props => getArrowRotation(props.$direction)};
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
  flex-shrink: 0;
`;

const ArrowLabel = styled.span`
  color: white;
  font-size: 13px;
  font-weight: 600;
  background: ${THEME.accentPrimary};
  padding: 6px 12px;
  border-radius: 4px;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const TooltipContainer = styled.div`
  position: fixed;
  z-index: 10000;
  pointer-events: all;
  cursor: ${props => props.$isDragging ? 'grabbing' : 'grab'};
  user-select: none;

  ${props => {
    switch (props.position) {
      case 'right':
        return `
          left: 270px;
          top: 50%;
        `;
      case 'left':
        return `
          right: 340px;
          top: 50%;
        `;
      case 'bottom':
        return `
          top: 80px;
          left: 50%;
        `;
      case 'center':
      default:
        // Anchor to lower-left of canvas area (left panel is 250px, header is 60px)
        return `
          bottom: 20px;
          left: 300px;
        `;
    }
  }}
`;

const DragHint = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  color: ${THEME.textSecondary};
  opacity: 0.6;
  white-space: nowrap;
`;

const Tooltip = styled.div`
  background: ${THEME.panelBackground};
  border: 2px solid ${THEME.accentPrimary};
  border-radius: 12px;
  width: 440px;
  max-width: 90vw;
  box-shadow: 0 10px 40px rgba(220, 20, 60, 0.4);
`;

const TooltipHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 20px;
  border-bottom: 1px solid #404040;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModuleLabel = styled.div`
  color: ${THEME.accentPrimary};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StepIndicator = styled.div`
  color: ${THEME.textMuted};
  font-size: 11px;
`;

const PauseButton = styled.button`
  background: transparent;
  border: 1px solid #505050;
  color: ${THEME.textSecondary};
  font-size: 12px;
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: ${THEME.textPrimary};
    background: rgba(255, 255, 255, 0.05);
    border-color: #606060;
  }
`;

const SkipButton = styled.button`
  background: transparent;
  border: none;
  color: ${THEME.textMuted};
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    color: ${THEME.textPrimary};
    background: rgba(255, 255, 255, 0.05);
  }
`;

const TooltipContent = styled.div`
  padding: 24px;
`;

const TooltipTitle = styled.h3`
  color: ${THEME.textPrimary};
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 12px 0;
`;

const TooltipDescription = styled.p`
  color: ${THEME.textSecondary};
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
`;

const ActionHint = styled.div`
  margin-top: 16px;
  padding: 10px 14px;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 6px;
  color: #FFC107;
  font-size: 13px;
  font-weight: 500;
`;

const ActionComplete = styled.div`
  margin-top: 16px;
  padding: 10px 14px;
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 6px;
  color: #4CAF50;
  font-size: 13px;
  font-weight: 500;
`;

const TooltipFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #404040;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProgressDots = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  max-width: 200px;
`;

const Dot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${props => props.$active ? THEME.accentPrimary : props.$completed ? '#606060' : '#404040'};
  transition: all 0.3s ease;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  outline: none;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: ${THEME.accentPrimary};
  color: white;

  &:hover:not(:disabled) {
    background: #FF1744;
    box-shadow: 0 4px 12px rgba(220, 20, 60, 0.4);
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  color: ${THEME.textSecondary};
  border: 1px solid #404040;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.05);
    color: ${THEME.textPrimary};
  }
`;

export default TutorialOverlay;
export { tutorialSteps };
